import { useState, useEffect } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import './App.css';
import AuthModal from './AuthModal';
import NetworkPage from './NetworkPage';
import AdminPanel from './AdminPanel';
import { supabase } from './supabase';
import { ImpersonationContext } from './ImpersonationContext';

function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuth, setShowAuth] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [walletAddress, setWalletAddress] = useState('');
  const [page, setPage] = useState('home');
  const [impersonating, setImpersonating] = useState(null);
  const { disconnect, connected, publicKey } = useWallet();

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase.from('profiles').select('*').eq('id', userId).single();
      console.log('[PROFILE]', data, error);
      if (data) {
        if (!data.approved) {
          await supabase.auth.signOut();
          setUser(null); setProfile(null); setPage('home'); return;
        }
        if (data.plan_expires_at && new Date(data.plan_expires_at) < new Date()) {
          await supabase.auth.signOut();
          setUser(null); setProfile(null); setPage('home'); return;
        }
        if (data.sim_balance !== null && data.sim_balance !== undefined) {
          localStorage.setItem('sim-wallet-balance', String(data.sim_balance));
        }
        setProfile(data);
      }
    } catch(e) { console.error('[PROFILE ERROR]', e); }
  };

  useEffect(() => {
    // Synchroniser la clé API Helius depuis Supabase au démarrage
    supabase.from('settings').select('value').eq('key', 'helius-api-key').single()
      .then(({ data }) => { if (data?.value) localStorage.setItem('helius-api-key', data.value); })
      .catch(() => {});

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
        setPage('network');
      }
      setLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser(session.user);
        fetchProfile(session.user.id);
        setPage('network');
      } else {
        setUser(null); setProfile(null); setPage('home');
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (connected && publicKey) setWalletAddress(publicKey.toBase58());
  }, [connected, publicKey]);

  const handleAuth = (authUser) => {
    setUser(authUser);
    fetchProfile(authUser.id);
    setPage('network');
    setShowAuth(false);
  };

  const handleDisconnect = async () => {
    await supabase.auth.signOut();
    await disconnect();
    setUser(null); setProfile(null);
    setWalletAddress(''); setPage('home');
    setImpersonating(null);
  };

  if (loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',background:'#060809',color:'#00e5cc',fontSize:14,fontFamily:'monospace'}}>
      Chargement...
    </div>
  );

  if (page === 'network') {
    return (
      <>
        {showAdmin && profile?.is_admin && (
          <AdminPanel
            user={user}
            onClose={()=>setShowAdmin(false)}
            onImpersonate={(targetUser) => {
              setImpersonating(targetUser);
              setShowAdmin(false);
              supabase.from('admin_logs').insert({
                admin_id: user.id,
                admin_email: user.email,
                target_user_id: targetUser.id,
                target_email: targetUser.email,
                action: 'impersonate_start',
              });
            }}
          />
        )}

        {/*  Barre mode impersonation */}
        {impersonating && profile?.is_admin && (
          <div style={{position:'fixed',bottom:0,left:0,right:0,zIndex:9999,background:'linear-gradient(135deg,#f0a500,#ff6b00)',padding:'6px 20px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontSize:14}}></span>
              <span style={{fontSize:12,fontWeight:700,color:'#060809'}}>MODE ADMIN — Vous voyez l'interface de : {impersonating.email}</span>
            </div>
            <button onClick={()=>setImpersonating(null)}
              style={{padding:'5px 14px',borderRadius:6,background:'rgba(0,0,0,0.2)',border:'1px solid rgba(0,0,0,0.3)',color:'#060809',fontSize:11,fontWeight:700,cursor:'pointer'}}>
              ← Retour admin
            </button>
          </div>
        )}

        <ImpersonationContext.Provider value={{ effectiveUserId: impersonating?.id || null, isImpersonating: !!impersonating }}>
          <NetworkPage
            walletAddress={walletAddress}
            user={impersonating ? {id: impersonating.id, email: impersonating.email} : user}
            profile={impersonating ? impersonating : profile}
            onDisconnect={handleDisconnect}
            onGoHome={() => setPage('home')}
            onOpenAdmin={profile?.is_admin ? (targetUser)=>{ if(targetUser){ setImpersonating(targetUser); } else { setShowAdmin(true); } } : null}
            impersonating={impersonating}
          />
        </ImpersonationContext.Provider>
      </>
    );
  }

  return (
    <div className="app">
      {showAuth && <AuthModal onClose={()=>setShowAuth(false)} onAuth={handleAuth}/>}
      {showAdmin && profile?.is_admin && <AdminPanel user={user} onClose={()=>setShowAdmin(false)}/>}

      <nav className="navbar">
        <div className="nav-logo">COPY<span className="dot">CC</span>XX</div>
        <div className="nav-actions">
          {profile?.is_admin && (
            <button onClick={()=>setShowAdmin(true)}
              style={{padding:'6px 12px',borderRadius:6,background:'rgba(124,77,255,0.15)',border:'1px solid #7c4dff33',color:'#7c4dff',fontSize:11,fontWeight:700,cursor:'pointer'}}>
               Admin
            </button>
          )}
          <button className="btn-login"onClick={()=>setShowAuth(true)}>Connexion</button>
          <button className="btn-signup"onClick={()=>setShowAuth(true)}>
            <svg viewBox="0 0 20 20"fill="none"width="16"height="16">
              <path d="M10 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zm-6 9a6 6 0 0 1 12 0v1H4v-1z"fill="currentColor"/>
            </svg>
            S'inscrire
          </button>
        </div>
      </nav>

      <main className="hero">
        <div className="hero-content">
          <h1 className="hero-title">
            On trouve ceux qui<br />font de l'argent.<br />
            <span className="hero-accent">Tu les copies.</span>
          </h1>
          <p className="hero-subtitle">
            Nous suivons en permanence les wallets les plus profitables<br />
            sur Solana. Tu choisis un groupe. Leurs mouvements deviennent les tiens.
          </p>
          <button className="btn-cta"onClick={()=>setShowAuth(true)}>Copier mon premier trade</button>
          <div className="hero-badges">
            <span>Accès sur invitation</span>
            <span className="dot-sep">·</span>
            <span>1% par transaction</span>
            <span className="dot-sep">·</span>
            <span>Premier trade offert</span>
          </div>
        </div>
        <div className="preview-cards">
          <div className="preview-card">
            <div className="card-header">
              <div className="card-avatar"style={{background:'#1a3a2a'}}></div>
              <div className="card-info">
                <div className="card-name">Wallet Alpha</div>
                <div className="card-tag">Meilleur Trader</div>
              </div>
              <div className="card-pnl positive">+284%</div>
            </div>
            <div className="card-chart">
              <svg viewBox="0 0 120 40"fill="none"width="100%">
                <polyline points="0,35 15,28 30,32 45,18 60,22 75,10 90,15 105,5 120,8"stroke="#00e5cc"strokeWidth="2"fill="none"/>
              </svg>
            </div>
            <div className="card-stats">
              <span className="stat"><span className="stat-label">Transactions</span><span className="stat-val">142</span></span>
              <span className="stat"><span className="stat-label">Taux victoire</span><span className="stat-val positive">73%</span></span>
              <span className="stat"><span className="stat-label">PnL Moyen</span><span className="stat-val positive">+18.4%</span></span>
            </div>
          </div>
          <div className="preview-card">
            <div className="card-header">
              <div className="card-avatar"style={{background:'#1a1a3a'}}></div>
              <div className="card-info">
                <div className="card-name">Wallet Beta</div>
                <div className="card-tag">Degen Confirmé</div>
              </div>
              <div className="card-pnl positive">+512%</div>
            </div>
            <div className="card-chart">
              <svg viewBox="0 0 120 40"fill="none"width="100%">
                <polyline points="0,38 15,35 30,30 45,25 60,15 75,18 90,8 105,4 120,2"stroke="#00e5cc"strokeWidth="2"fill="none"/>
              </svg>
            </div>
            <div className="card-stats">
              <span className="stat"><span className="stat-label">Transactions</span><span className="stat-val">89</span></span>
              <span className="stat"><span className="stat-label">Taux victoire</span><span className="stat-val positive">81%</span></span>
              <span className="stat"><span className="stat-label">PnL Moyen</span><span className="stat-val positive">+31.2%</span></span>
            </div>
          </div>
          <div className="preview-card preview-card-blur">
            <div className="card-header">
              <div className="card-avatar"style={{background:'#2a1a1a'}}></div>
              <div className="card-info">
                <div className="card-name">Wallet Gamma</div>
                <div className="card-tag">Baleine</div>
              </div>
              <div className="card-pnl positive">+1,240%</div>
            </div>
            <div className="card-chart">
              <svg viewBox="0 0 120 40"fill="none"width="100%">
                <polyline points="0,36 15,30 30,20 45,24 60,12 75,6 90,10 105,3 120,1"stroke="#00e5cc"strokeWidth="2"fill="none"/>
              </svg>
            </div>
            <div className="card-stats">
              <span className="stat"><span className="stat-label">Transactions</span><span className="stat-val">203</span></span>
              <span className="stat"><span className="stat-label">Taux victoire</span><span className="stat-val positive">68%</span></span>
              <span className="stat"><span className="stat-label">PnL Moyen</span><span className="stat-val positive">+44.1%</span></span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;